import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { budgetsAPI, categoriesAPI, transactionsAPI } from '../services/api';
import StatusToast from './StatusToast';
import usePersistentUiState from '../hooks/usePersistentUiState';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];
const DEFAULT_CATEGORY_COLOR = '#94a3b8';
const RECENT_CATEGORY_HISTORY_MONTH_COUNT = 3;
const BUDGET_UI_STATE_KEY = 'receipt-tracker:ui:budget:v1';
const BUDGET_STATUS_FILTER_OPTIONS = ['all', 'good', 'warning', 'over', 'none'];

const buildCurrentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const buildDefaultBudgetUiState = () => ({
  selectedMonth: buildCurrentMonthValue(),
  categoryStatusFilter: 'all',
});

const sanitizeBudgetUiState = (value, fallbackState = buildDefaultBudgetUiState()) => {
  const nextState = value && typeof value === 'object' && !Array.isArray(value) ? value : {};

  return {
    ...fallbackState,
    selectedMonth: /^\d{4}-(0[1-9]|1[0-2])$/.test(String(nextState.selectedMonth || ''))
      ? String(nextState.selectedMonth)
      : fallbackState.selectedMonth,
    categoryStatusFilter: BUDGET_STATUS_FILTER_OPTIONS.includes(String(nextState.categoryStatusFilter || ''))
      ? String(nextState.categoryStatusFilter)
      : fallbackState.categoryStatusFilter,
  };
};

function BudgetManager() {
  const [persistedBudgetUiState, setPersistedBudgetUiState] = usePersistentUiState(
    BUDGET_UI_STATE_KEY,
    buildDefaultBudgetUiState,
    { storage: 'local', validate: sanitizeBudgetUiState }
  );
  const location = useLocation();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const monthFromQuery = new URLSearchParams(location.search).get('month');
    if (/^\d{4}-(0[1-9]|1[0-2])$/.test(monthFromQuery || '')) {
      return monthFromQuery;
    }
    return persistedBudgetUiState.selectedMonth;
  });

  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [isEditingTotal, setIsEditingTotal] = useState(false);
  const [tempBudget, setTempBudget] = useState('');
  const [isApplyFromSpendingOpen, setIsApplyFromSpendingOpen] = useState(false);
  const [applySourceMonth, setApplySourceMonth] = useState('');
  const [applyScope, setApplyScope] = useState('all');
  const [applyPreview, setApplyPreview] = useState(null);
  const [selectedApplyCategoryIds, setSelectedApplyCategoryIds] = useState([]);
  const [applyEditedAmounts, setApplyEditedAmounts] = useState({});
  const [isLoadingApplyPreview, setIsLoadingApplyPreview] = useState(false);
  const [isApplyingFromSpending, setIsApplyingFromSpending] = useState(false);
  const [activeCategoryBudgetId, setActiveCategoryBudgetId] = useState(null);
  const [activeCategoryBudgetName, setActiveCategoryBudgetName] = useState('');
  const [activeCategoryBudgetValue, setActiveCategoryBudgetValue] = useState('');
  const [isSavingCategoryBudget, setIsSavingCategoryBudget] = useState(false);
  const [recentCategoryActuals, setRecentCategoryActuals] = useState({
    months: [],
    spendingByMonth: {},
  });
  const [isLoadingRecentCategoryActuals, setIsLoadingRecentCategoryActuals] = useState(false);
  const [categoryStatusFilter, setCategoryStatusFilter] = useState(() => persistedBudgetUiState.categoryStatusFilter);
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const monthPickerRef = useRef(null);

  const [selectedYear, selectedMonthNumber] = selectedMonth.split('-').map(Number);

  const hexToRgba = (hexColor, alpha = 1) => {
    const safeHex = String(hexColor || '').replace('#', '');
    if (!/^[0-9A-Fa-f]{6}$/.test(safeHex)) {
      return `rgba(148,163,184,${alpha})`;
    }
    const r = parseInt(safeHex.slice(0, 2), 16);
    const g = parseInt(safeHex.slice(2, 4), 16);
    const b = parseInt(safeHex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Number(amount || 0));
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const startDate = `${selectedMonth}-01`;
      const daysInMonth = new Date(selectedYear, selectedMonthNumber, 0).getDate();
      const endDate = `${selectedMonth}-${String(daysInMonth).padStart(2, '0')}`;

      const [categoriesData, transactionsData, budgetsData] = await Promise.all([
        categoriesAPI.getAll(),
        transactionsAPI.getByDateRange(startDate, endDate),
        budgetsAPI.getAll(selectedMonthNumber, selectedYear),
      ]);

      setCategories(categoriesData);
      setTransactions(transactionsData);
      setBudgets(budgetsData);
    } catch (err) {
      console.error('Error loading budget data:', err);
      setError('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedMonthNumber, selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const monthFromQuery = new URLSearchParams(location.search).get('month');
    if (/^\d{4}-(0[1-9]|1[0-2])$/.test(monthFromQuery || '') && monthFromQuery !== selectedMonth) {
      setSelectedMonth(monthFromQuery);
    }
  }, [location.search, selectedMonth]);

  useEffect(() => {
    setPersistedBudgetUiState({
      selectedMonth,
      categoryStatusFilter,
    });
  }, [categoryStatusFilter, selectedMonth, setPersistedBudgetUiState]);

  useEffect(() => {
    let isCancelled = false;

    const buildPriorMonthMeta = (monthsBack) => {
      const [year, month] = selectedMonth.split('-').map(Number);
      const date = new Date(year, month - 1 - monthsBack, 1);
      const resolvedYear = date.getFullYear();
      const resolvedMonth = date.getMonth() + 1;
      const key = `${resolvedYear}-${String(resolvedMonth).padStart(2, '0')}`;
      return {
        key,
        year: resolvedYear,
        month: resolvedMonth,
        shortLabel: date.toLocaleDateString('en-US', { month: 'short' }),
      };
    };

    const loadRecentCategoryActuals = async () => {
      setIsLoadingRecentCategoryActuals(true);
      try {
        const months = Array.from(
          { length: RECENT_CATEGORY_HISTORY_MONTH_COUNT },
          (_, index) => buildPriorMonthMeta(RECENT_CATEGORY_HISTORY_MONTH_COUNT - index)
        );

        const monthlyTransactionData = await Promise.all(
          months.map(async (monthMeta) => {
            const startDate = `${monthMeta.key}-01`;
            const daysInMonth = new Date(monthMeta.year, monthMeta.month, 0).getDate();
            const endDate = `${monthMeta.key}-${String(daysInMonth).padStart(2, '0')}`;
            const monthTransactions = await transactionsAPI.getByDateRange(startDate, endDate);
            return { monthMeta, monthTransactions };
          })
        );

        if (isCancelled) {
          return;
        }

        const spendingByMonth = monthlyTransactionData.reduce((accumulator, monthEntry) => {
          const monthCategorySpending = {};
          monthEntry.monthTransactions.forEach((transaction) => {
            const categoryId = String(transaction.categoryId || '');
            if (!categoryId) {
              return;
            }
            monthCategorySpending[categoryId] =
              (monthCategorySpending[categoryId] || 0) + Number(transaction.itemTotal || 0);
          });
          accumulator[monthEntry.monthMeta.key] = monthCategorySpending;
          return accumulator;
        }, {});

        setRecentCategoryActuals({
          months,
          spendingByMonth,
        });
      } catch (err) {
        if (!isCancelled) {
          console.error('Error loading recent category actuals:', err);
          setRecentCategoryActuals({
            months: [],
            spendingByMonth: {},
          });
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingRecentCategoryActuals(false);
        }
      }
    };

    loadRecentCategoryActuals();

    return () => {
      isCancelled = true;
    };
  }, [selectedMonth]);

  const totalBudgetEntry = useMemo(() => {
    return budgets.find((budget) => !budget.categoryId) || null;
  }, [budgets]);

  const categoriesById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      map.set(String(category.id), category);
    });
    return map;
  }, [categories]);

  const topLevelCategories = useMemo(() => {
    return categories
      .filter((category) => !category.parentCategoryId)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [categories]);

  const childCategoriesByParentId = useMemo(() => {
    const map = categories
      .filter((category) => category.parentCategoryId)
      .reduce((acc, category) => {
        const parentId = String(category.parentCategoryId);
        if (!acc[parentId]) {
          acc[parentId] = [];
        }
        acc[parentId].push(category);
        return acc;
      }, {});

    Object.keys(map).forEach((parentId) => {
      map[parentId].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    });

    return map;
  }, [categories]);

  const categoryBudgetMap = useMemo(() => {
    return budgets.reduce((map, budget) => {
      if (budget.categoryId) {
        map[budget.categoryId] = budget;
      }
      return map;
    }, {});
  }, [budgets]);

  const spendingByCategory = useMemo(() => {
    const spending = {};

    transactions.forEach((transaction) => {
      if (!spending[transaction.categoryId]) {
        spending[transaction.categoryId] = 0;
      }
      spending[transaction.categoryId] += Number(transaction.itemTotal || 0);
    });

    return spending;
  }, [transactions]);

  const totalSpent = useMemo(() => {
    return transactions.reduce((sum, transaction) => sum + Number(transaction.itemTotal || 0), 0);
  }, [transactions]);

  const totalCategoryBudget = useMemo(() => {
    return budgets.reduce((sum, budget) => {
      if (!budget.categoryId) {
        return sum;
      }
      return sum + Number(budget.amount || 0);
    }, 0);
  }, [budgets]);
  const totalBudgetAmount = totalBudgetEntry ? Number(totalBudgetEntry.amount) : 0;

  const activeCategoryRecentSpending = useMemo(() => {
    const categoryId = String(activeCategoryBudgetId || '');
    if (!categoryId) {
      return null;
    }

    const monthlyActuals = recentCategoryActuals.months.map((monthMeta) => ({
      key: monthMeta.key,
      label: monthMeta.shortLabel,
      amount: Number(recentCategoryActuals.spendingByMonth[monthMeta.key]?.[categoryId] || 0),
    }));

    const averageAmount = monthlyActuals.length > 0
      ? monthlyActuals.reduce((sum, item) => sum + item.amount, 0) / monthlyActuals.length
      : 0;

    return {
      monthlyActuals,
      averageAmount,
    };
  }, [activeCategoryBudgetId, recentCategoryActuals]);

  const handleEditTotal = () => {
    setIsEditingTotal(true);
    setTempBudget((totalBudgetEntry ? Number(totalBudgetEntry.amount) : 0).toString());
  };

  const handleSaveTotal = async () => {
    const amount = parseFloat(tempBudget);

    if (!Number.isNaN(amount) && amount > 0) {
      if (totalCategoryBudget > amount) {
        setError(
          `Total monthly budget cannot be less than category budgets (${formatCurrency(totalCategoryBudget)}).`
        );
        return;
      }

      try {
        if (totalBudgetEntry) {
          await budgetsAPI.update(totalBudgetEntry.id, { amount });
        } else {
          await budgetsAPI.create({
            amount,
            month: selectedMonthNumber,
            year: selectedYear,
            categoryId: null,
          });
        }

        await fetchData();
      } catch (err) {
        console.error('Error saving total budget:', err);
        setError(err.response?.data?.error || 'Failed to save total budget');
      }
    }

    setIsEditingTotal(false);
  };

  const handleCancelTotal = () => {
    setIsEditingTotal(false);
    setTempBudget('');
  };

  const openCategoryBudgetEditor = useCallback((categoryId) => {
    const categoryIdString = String(categoryId || '');
    if (!categoryIdString) {
      return;
    }
    const category = categoriesById.get(categoryIdString);
    if (!category) {
      return;
    }
    const existingBudget = categoryBudgetMap[categoryIdString];
    setActiveCategoryBudgetId(categoryIdString);
    setActiveCategoryBudgetName(String(category.name || 'Category'));
    setActiveCategoryBudgetValue(
      existingBudget ? String(Number(existingBudget.amount || 0)) : ''
    );
  }, [categoriesById, categoryBudgetMap]);

  const closeCategoryBudgetEditor = useCallback(() => {
    setActiveCategoryBudgetId(null);
    setActiveCategoryBudgetName('');
    setActiveCategoryBudgetValue('');
    setIsSavingCategoryBudget(false);
  }, []);

  const handleSaveCategoryBudget = useCallback(async () => {
    const categoryId = String(activeCategoryBudgetId || '');
    if (!categoryId) {
      return;
    }

    const amount = Number(activeCategoryBudgetValue);
    if (Number.isNaN(amount) || amount < 0) {
      setError('Enter a valid category budget amount.');
      return;
    }

    if (amount > 0 && (!totalBudgetAmount || totalBudgetAmount <= 0)) {
      setError('Set a total monthly budget before assigning category budgets.');
      return;
    }

    const existingBudget = categoryBudgetMap[categoryId];
    const existingAmount = existingBudget ? Number(existingBudget.amount || 0) : 0;
    const projectedCategoryTotal = totalCategoryBudget - existingAmount + amount;
    if (projectedCategoryTotal > totalBudgetAmount) {
      setError(
        `Category budgets cannot exceed total monthly budget (${formatCurrency(totalBudgetAmount)}).`
      );
      return;
    }

    try {
      setIsSavingCategoryBudget(true);
      setError('');
      if (existingBudget) {
        await budgetsAPI.update(existingBudget.id, { amount });
      } else {
        await budgetsAPI.create({
          amount,
          month: selectedMonthNumber,
          year: selectedYear,
          categoryId,
        });
      }
      await fetchData();
      closeCategoryBudgetEditor();
    } catch (err) {
      console.error('Error saving category budget:', err);
      setError(err.response?.data?.error || 'Failed to save category budget.');
    } finally {
      setIsSavingCategoryBudget(false);
    }
  }, [
    activeCategoryBudgetId,
    activeCategoryBudgetValue,
    categoryBudgetMap,
    closeCategoryBudgetEditor,
    fetchData,
    selectedMonthNumber,
    selectedYear,
    totalBudgetAmount,
    totalCategoryBudget,
  ]);

  const groupedParentBudgets = useMemo(() => {
    const groups = topLevelCategories.map((parentCategory) => {
      const childCategories = childCategoriesByParentId[String(parentCategory.id)] || [];
      const groupCategories = [parentCategory, ...childCategories];

      const spent = groupCategories.reduce(
        (sum, category) => sum + Number(spendingByCategory[category.id] || 0),
        0
      );
      const budget = groupCategories.reduce((sum, category) => {
        const amount = categoryBudgetMap[category.id]
          ? Number(categoryBudgetMap[category.id].amount || 0)
          : 0;
        return sum + amount;
      }, 0);

      return {
        parentCategory,
        childCategories,
        spent,
        budget,
      };
    });

    const usage = (group) => (group.budget > 0 ? group.spent / group.budget : -1);
    const isOverBudget = (group) => group.budget > 0 && group.spent > group.budget;

    return [...groups].sort((a, b) => {
      const overDiff = Number(isOverBudget(b)) - Number(isOverBudget(a));
      if (overDiff !== 0) return overDiff;
      return usage(b) - usage(a) || b.spent - a.spent;
    });
  }, [topLevelCategories, childCategoriesByParentId, spendingByCategory, categoryBudgetMap]);

  const getBudgetStatus = (spent, budget) => {
    if (!budget || budget === 0) {
      return { percentage: 0, status: 'none' };
    }

    const percentage = (spent / budget) * 100;

    if (percentage >= 100) {
      return { percentage: 100, status: 'over' };
    }
    if (percentage >= 80) {
      return { percentage, status: 'warning' };
    }

    return { percentage, status: 'good' };
  };

  const getBudgetStatusMeta = useCallback((status) => {
    switch (status) {
      case 'over':
        return {
          barClass: 'bg-red-500',
          textClass: 'text-red-700',
          badgeClass: 'bg-red-50 text-red-700 ring-red-200',
          badgeLabel: 'Over budget',
        };
      case 'warning':
        return {
          barClass: 'bg-amber-500',
          textClass: 'text-amber-700',
          badgeClass: 'bg-amber-50 text-amber-700 ring-amber-200',
          badgeLabel: 'Near limit',
        };
      case 'none':
        return {
          barClass: 'bg-gray-300',
          textClass: 'text-gray-500',
          badgeClass: 'bg-gray-100 text-gray-600 ring-gray-200',
          badgeLabel: 'No budget',
        };
      case 'good':
      default:
        return {
          barClass: 'bg-emerald-500',
          textClass: 'text-emerald-700',
          badgeClass: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
          badgeLabel: 'On track',
        };
    }
  }, []);

  const getBudgetStatusText = useCallback((spent, budget, status) => {
    if (!budget || budget <= 0) {
      return 'Set a budget to track this category.';
    }
    if (status === 'over') {
      return `Over by ${formatCurrency(spent - budget)}`;
    }
    return `${formatCurrency(Math.max(budget - spent, 0))} left`;
  }, []);

  const getRingAccentColor = useCallback((status, preferredColor) => {
    if (status === 'over') return '#ef4444';
    if (status === 'warning') return '#f59e0b';
    if (status === 'none') return '#9ca3af';
    return preferredColor || '#10b981';
  }, []);

  const totalBudgetStatus = getBudgetStatus(totalSpent, totalBudgetAmount);
  const totalBudgetStatusMeta = getBudgetStatusMeta(totalBudgetStatus.status);
  const totalRingAccentColor = getRingAccentColor(totalBudgetStatus.status, '#2563eb');
  const totalBudgetStatusText = getBudgetStatusText(totalSpent, totalBudgetAmount, totalBudgetStatus.status);
  const overviewMainCategoryTiles = groupedParentBudgets.map(({ parentCategory, childCategories, spent, budget }) => {
    const hasChildCategories = childCategories.length > 0;
    const parentSpentAmount = Number(spendingByCategory[parentCategory.id] || 0);
    const parentBudgetEntry = categoryBudgetMap[parentCategory.id] || null;
    const parentBudgetAmount = parentBudgetEntry
      ? Number(parentBudgetEntry.amount || 0)
      : 0;
    const spentAmount = hasChildCategories ? spent : parentSpentAmount;
    const budgetAmount = hasChildCategories ? budget : parentBudgetAmount;
    const status = getBudgetStatus(spentAmount, budgetAmount);
    const statusMeta = getBudgetStatusMeta(status.status);
    const accentColor = getRingAccentColor(status.status, parentCategory.color || DEFAULT_CATEGORY_COLOR);
    const statusText = getBudgetStatusText(spentAmount, budgetAmount, status.status);
    const hasBudget = budgetAmount > 0;
    const deltaAmount = status.status === 'over'
      ? Math.max(spentAmount - budgetAmount, 0)
      : Math.max(budgetAmount - spentAmount, 0);
    const deltaLabel = status.status === 'over'
      ? `Over ${formatCurrency(deltaAmount)}`
      : `Left ${formatCurrency(deltaAmount)}`;
    return {
      id: parentCategory.id,
      name: parentCategory.name,
      accentColor,
      status,
      statusMeta,
      statusText,
      spentAmount,
      budgetAmount,
      hasBudget,
      hasDirectBudget: Boolean(parentBudgetEntry),
      deltaLabel,
    };
  });
  const categoryStatusFilterOptions = useMemo(() => ([
    { value: 'all', label: 'All' },
    { value: 'good', label: 'On track' },
    { value: 'warning', label: 'Near limit' },
    { value: 'over', label: 'Over budget' },
    { value: 'none', label: 'No budget' },
  ]), []);
  const filteredOverviewMainCategoryTiles = useMemo(() => {
    if (categoryStatusFilter === 'all') {
      return overviewMainCategoryTiles;
    }
    return overviewMainCategoryTiles.filter((tile) => tile.status.status === categoryStatusFilter);
  }, [overviewMainCategoryTiles, categoryStatusFilter]);

  const getMonthName = (monthString) => {
    const [year, month] = monthString.split('-');
    const date = new Date(year, parseInt(month, 10) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const changeMonthBy = useCallback((delta) => {
    setSelectedMonth((current) => {
      const [year, month] = current.split('-').map(Number);
      const nextDate = new Date(year, month - 1 + delta, 1);
      return `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`;
    });
  }, []);

  const openMonthPicker = useCallback(() => {
    setPickerYear(selectedYear);
    setIsMonthPickerOpen(true);
  }, [selectedYear]);

  const handleMonthGridSelect = useCallback((monthNumber) => {
    setSelectedMonth(`${pickerYear}-${String(monthNumber).padStart(2, '0')}`);
    setIsMonthPickerOpen(false);
  }, [pickerYear]);

  const getPreviousMonthString = useCallback((monthString) => {
    const [year, month] = monthString.split('-').map(Number);
    const prev = new Date(year, month - 2, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const openApplyFromSpendingModal = useCallback(() => {
    setApplySourceMonth(getPreviousMonthString(selectedMonth));
    setApplyScope('all');
    setApplyPreview(null);
    setSelectedApplyCategoryIds([]);
    setApplyEditedAmounts({});
    setIsApplyFromSpendingOpen(true);
  }, [getPreviousMonthString, selectedMonth]);

  const closeApplyFromSpendingModal = useCallback(() => {
    setIsApplyFromSpendingOpen(false);
    setApplyPreview(null);
    setSelectedApplyCategoryIds([]);
    setApplyEditedAmounts({});
    setIsLoadingApplyPreview(false);
    setIsApplyingFromSpending(false);
  }, []);

  const handleBackFromApplyPreview = useCallback(() => {
    setApplyPreview(null);
    setSelectedApplyCategoryIds([]);
    setApplyEditedAmounts({});
    setError('');
  }, []);

  const runApplyFromSpendingRequest = useCallback(async (dryRun, options = {}) => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(applySourceMonth || '')) {
      setError('Select a valid source month.');
      return null;
    }

    const [sourceYear, sourceMonth] = applySourceMonth.split('-').map(Number);
    const selectedCategoryIds = Array.isArray(options.selectedCategoryIds)
      ? options.selectedCategoryIds.filter(Boolean)
      : null;
    const categoryOverrides = Array.isArray(options.categoryOverrides)
      ? options.categoryOverrides
      : null;
    const result = await budgetsAPI.applyFromSpending({
      sourceMonth,
      sourceYear,
      targetMonth: selectedMonthNumber,
      targetYear: selectedYear,
      scope: applyScope,
      ...(selectedCategoryIds ? { selectedCategoryIds } : {}),
      ...(categoryOverrides ? { categoryOverrides } : {}),
      dryRun,
    });

    return result?.preview || null;
  }, [
    applySourceMonth,
    applyScope,
    selectedMonthNumber,
    selectedYear,
  ]);

  const handlePreviewApplyFromSpending = useCallback(async () => {
    try {
      setIsLoadingApplyPreview(true);
      setError('');
      const preview = await runApplyFromSpendingRequest(true);
      if (preview) {
        setApplyPreview(preview);
        setSelectedApplyCategoryIds(
          preview.categories.map((item) => String(item.categoryId))
        );
        setApplyEditedAmounts(
          preview.categories.reduce((acc, item) => {
            acc[String(item.categoryId)] = String(Number(item.proposedAmount || 0));
            return acc;
          }, {})
        );
      }
    } catch (err) {
      console.error('Error previewing apply-from-spending:', err);
      setError(err.response?.data?.error || 'Failed to generate preview.');
    } finally {
      setIsLoadingApplyPreview(false);
    }
  }, [runApplyFromSpendingRequest]);

  const handleApplyFromSpending = useCallback(async () => {
    try {
      setIsApplyingFromSpending(true);
      setError('');
      const selectedCategoryIds = selectedApplyCategoryIds
        .map((categoryId) => String(categoryId || ''))
        .filter(Boolean);
      if (selectedCategoryIds.length === 0) {
        setError('Select at least one category to apply.');
        return;
      }
      const negativeEditedCategoryIds = selectedCategoryIds.filter((categoryId) => {
        const rawValue = applyEditedAmounts[String(categoryId)];
        const parsed = Number(rawValue);
        return !Number.isNaN(parsed) && parsed < 0;
      });
      if (negativeEditedCategoryIds.length > 0) {
        setError('Category amounts cannot be negative.');
        return;
      }

      const effectiveSelectedCategoryIds = selectedCategoryIds.filter((categoryId) => {
        const rawValue = applyEditedAmounts[String(categoryId)];
        const parsed = Number(rawValue);
        return !Number.isNaN(parsed) && parsed > 0;
      });

      // Treat zero-valued selections as skipped categories (same outcome as unselected).
      if (effectiveSelectedCategoryIds.length === 0) {
        await fetchData();
        setIsApplyFromSpendingOpen(false);
        return;
      }

      const effectiveSelectedSet = new Set(effectiveSelectedCategoryIds);
      const categoryOverrides = Object.entries(applyEditedAmounts)
        .filter(([categoryId, amount]) => {
          const parsed = Number(amount);
          return effectiveSelectedSet.has(String(categoryId)) && !Number.isNaN(parsed) && parsed > 0;
        })
        .map(([categoryId, amount]) => ({
          categoryId: String(categoryId),
          proposedAmount: Number(amount),
        }));

      const preview = await runApplyFromSpendingRequest(false, {
        selectedCategoryIds: effectiveSelectedCategoryIds,
        categoryOverrides,
      });
      if (preview) {
        setApplyPreview(preview);
      }
      await fetchData();
      setIsApplyFromSpendingOpen(false);
    } catch (err) {
      console.error('Error applying budgets from spending:', err);
      setError(err.response?.data?.error || 'Failed to apply budgets from prior month spending.');
    } finally {
      setIsApplyingFromSpending(false);
    }
  }, [runApplyFromSpendingRequest, fetchData, selectedApplyCategoryIds, applyEditedAmounts]);

  const applyPreviewGroupedCategories = useMemo(() => {
    if (!applyPreview?.categories?.length) {
      return [];
    }

    const groups = new Map();
    const normalizeName = (value) => String(value || '').trim().toLowerCase();

    applyPreview.categories.forEach((item) => {
      const category = categoriesById.get(String(item.categoryId)) || null;
      const parentCategory = category?.parentCategoryId
        ? categoriesById.get(String(category.parentCategoryId)) || null
        : category;
      const groupKey = parentCategory
        ? `parent-${String(parentCategory.id)}`
        : `orphan-${String(item.categoryId)}`;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          parentCategory,
          items: [],
        });
      }

      groups.get(groupKey).items.push({
        ...item,
        category,
      });
    });

    const groupValues = [...groups.values()];
    const subcategoryNameSet = new Set(
      groupValues
        .flatMap((group) => group.items)
        .filter((item) => Boolean(item.category?.parentCategoryId))
        .map((item) => normalizeName(item.category?.name))
        .filter(Boolean)
    );

    return groupValues
      .map((group) => {
        const parentCategoryId = group.parentCategory ? String(group.parentCategory.id) : null;
        const parentItem = parentCategoryId
          ? group.items.find((item) => String(item.category?.id || '') === parentCategoryId) || null
          : null;
        const childItems = group.items
          .filter((item) => !parentItem || String(item.category?.id || '') !== parentCategoryId)
          .sort((a, b) =>
            String(a.category?.name || '').localeCompare(String(b.category?.name || ''))
          );
        const orderedItems = [
          ...(parentItem ? [parentItem] : []),
          ...childItems,
        ];
        const hasSubcategories = orderedItems.some((item) => Boolean(item.category?.parentCategoryId));
        const filteredItems = orderedItems.filter((item) => {
          const isSubcategory = Boolean(item.category?.parentCategoryId);
          if (isSubcategory) {
            return true;
          }
          if (hasSubcategories) {
            return true;
          }
          const nameKey = normalizeName(item.category?.name);
          if (!nameKey) {
            return true;
          }
          return !subcategoryNameSet.has(nameKey);
        });
        const parentName = String(
          group.parentCategory?.name ||
            parentItem?.category?.name ||
            childItems[0]?.category?.name ||
            'Unknown category'
        );

        return {
          ...group,
          parentName,
          hasSubcategories: filteredItems.some((item) => Boolean(item.category?.parentCategoryId)),
          items: filteredItems,
        };
      })
      .filter((group) => group.items.length > 0)
      .sort((a, b) => a.parentName.localeCompare(b.parentName));
  }, [applyPreview, categoriesById]);

  const visibleApplyPreviewCategoryIds = useMemo(() => {
    return applyPreviewGroupedCategories
      .flatMap((group) => group.items.map((item) => String(item.categoryId)))
      .filter(Boolean);
  }, [applyPreviewGroupedCategories]);

  const selectedVisibleApplyCategoryIds = useMemo(() => {
    const visibleSet = new Set(visibleApplyPreviewCategoryIds);
    return selectedApplyCategoryIds.filter((categoryId) => visibleSet.has(String(categoryId)));
  }, [selectedApplyCategoryIds, visibleApplyPreviewCategoryIds]);

  const areAllVisibleApplyCategoriesSelected =
    visibleApplyPreviewCategoryIds.length > 0 &&
    selectedVisibleApplyCategoryIds.length === visibleApplyPreviewCategoryIds.length;

  const selectedApplySummary = useMemo(() => {
    if (!applyPreview?.categories?.length) {
      return {
        selectedCount: 0,
        selectedTotal: 0,
      };
    }

    const selectedSet = new Set(selectedVisibleApplyCategoryIds.map((value) => String(value)));
    const selectedItems = applyPreview.categories.filter((item) =>
      selectedSet.has(String(item.categoryId))
    );
    const selectedTotal = selectedItems.reduce(
      (sum, item) => {
        const editedAmount = Number(applyEditedAmounts[String(item.categoryId)]);
        const nextAmount = Number.isNaN(editedAmount) ? Number(item.proposedAmount || 0) : editedAmount;
        return sum + Number(nextAmount || 0);
      },
      0
    );

    return {
      selectedCount: selectedItems.length,
      selectedTotal,
    };
  }, [applyPreview, selectedVisibleApplyCategoryIds, applyEditedAmounts]);

  const handleApplyPreviewAmountChange = useCallback((categoryId, value) => {
    const categoryIdString = String(categoryId || '');
    if (!categoryIdString) {
      return;
    }

    setApplyEditedAmounts((current) => ({
      ...current,
      [categoryIdString]: value,
    }));
  }, []);

  const toggleApplyPreviewCategorySelection = useCallback((categoryId) => {
    const categoryIdString = String(categoryId || '');
    if (!categoryIdString) {
      return;
    }
    setSelectedApplyCategoryIds((current) => {
      if (current.includes(categoryIdString)) {
        return current.filter((id) => id !== categoryIdString);
      }
      return [...current, categoryIdString];
    });
  }, []);

  const toggleSelectAllApplyPreviewCategories = useCallback(() => {
    if (areAllVisibleApplyCategoriesSelected) {
      setSelectedApplyCategoryIds((current) => {
        const visibleSet = new Set(visibleApplyPreviewCategoryIds.map((id) => String(id)));
        return current.filter((id) => !visibleSet.has(String(id)));
      });
      return;
    }

    setSelectedApplyCategoryIds((current) => {
      const merged = new Set(current.map((id) => String(id)));
      visibleApplyPreviewCategoryIds.forEach((id) => merged.add(String(id)));
      return [...merged];
    });
  }, [areAllVisibleApplyCategoriesSelected, visibleApplyPreviewCategoryIds]);

  useEffect(() => {
    if (!applyPreview) {
      return;
    }

    setSelectedApplyCategoryIds((current) => {
      const visibleIds = [...new Set(visibleApplyPreviewCategoryIds.map((id) => String(id)))];
      if (current.length === 0) {
        return visibleIds;
      }

      const visibleSet = new Set(visibleIds);
      const kept = current.filter((id) => visibleSet.has(String(id)));
      if (kept.length > 0) {
        return kept;
      }

      return visibleIds;
    });
  }, [applyPreview, visibleApplyPreviewCategoryIds]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMonthPickerOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (!isMonthPickerOpen) {
      return undefined;
    }

    const handleOutsidePress = (event) => {
      const pickerNode = monthPickerRef.current;
      if (!pickerNode) {
        return;
      }
      if (!pickerNode.contains(event.target)) {
        setIsMonthPickerOpen(false);
      }
    };

    window.addEventListener('mousedown', handleOutsidePress);
    window.addEventListener('touchstart', handleOutsidePress);
    return () => {
      window.removeEventListener('mousedown', handleOutsidePress);
      window.removeEventListener('touchstart', handleOutsidePress);
    };
  }, [isMonthPickerOpen]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
          <p className="text-gray-600">Loading budget...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="relative z-40 mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl">Budget</h1>
            <p className="mt-1 text-sm text-gray-500 sm:text-base">Set and track your spending limits</p>
          </div>
          <div ref={monthPickerRef} className="relative shrink-0">
            <div className="inline-flex items-center rounded-xl border border-gray-300 bg-white p-1">
              <button
                type="button"
                onClick={() => changeMonthBy(-1)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-700 transition hover:bg-gray-100"
                aria-label="Previous month"
              >
                <span aria-hidden="true">&lt;</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isMonthPickerOpen) {
                    setIsMonthPickerOpen(false);
                  } else {
                    openMonthPicker();
                  }
                }}
                className="rounded-lg px-2.5 py-1 text-xs font-semibold whitespace-nowrap text-gray-800 transition hover:bg-gray-100 sm:text-sm"
                aria-label="Open month picker"
                aria-expanded={isMonthPickerOpen}
              >
                {getMonthName(selectedMonth)}
              </button>
              <button
                type="button"
                onClick={() => changeMonthBy(1)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-700 transition hover:bg-gray-100"
                aria-label="Next month"
              >
                <span aria-hidden="true">&gt;</span>
              </button>
            </div>

            {isMonthPickerOpen && (
              <div className="mt-2 w-[20rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-gray-200 bg-white p-3 shadow-xl sm:absolute sm:right-0 sm:top-full sm:z-50">
                <div className="mb-3 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setPickerYear((current) => current - 1)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100"
                    aria-label="Previous year"
                  >
                    <span aria-hidden="true">&lt;</span>
                  </button>
                  <p className="text-sm font-semibold text-gray-900">{pickerYear}</p>
                  <button
                    type="button"
                    onClick={() => setPickerYear((current) => current + 1)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100"
                    aria-label="Next year"
                  >
                    <span aria-hidden="true">&gt;</span>
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {MONTH_OPTIONS.map((month) => {
                    const candidate = `${pickerYear}-${String(month.value).padStart(2, '0')}`;
                    const isActive = candidate === selectedMonth;
                    return (
                      <button
                        key={`budget-picker-${pickerYear}-${month.value}`}
                        type="button"
                        onClick={() => handleMonthGridSelect(month.value)}
                        className={`rounded-lg px-2 py-2 text-sm font-medium transition ${
                          isActive
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {month.label.slice(0, 3)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3">
          <button
            type="button"
            onClick={openApplyFromSpendingModal}
            className="text-xs font-medium text-blue-700 underline-offset-2 hover:underline sm:text-sm"
          >
            Use Prior Month Spending
          </button>
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow sm:p-5">
        {isEditingTotal && (
          <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50/60 p-3.5">
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Monthly Budget Amount</label>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    value={tempBudget}
                    onChange={(e) => setTempBudget(e.target.value)}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="Enter amount"
                    autoFocus
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveTotal}
                  className="rounded-lg bg-green-600 px-4 py-2 text-white transition hover:bg-green-700"
                >
                  Save
                </button>
                <button
                  onClick={handleCancelTotal}
                  className="rounded-lg bg-gray-200 px-4 py-2 text-gray-700 transition hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="relative mb-3 rounded-2xl border border-gray-200 bg-white/90 p-4 shadow-sm">
          <div className="mb-2 flex items-start justify-between gap-2">
            <h3 className="truncate text-lg font-semibold text-gray-900 sm:text-xl">Budget Overview</h3>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset sm:text-[11px] ${totalBudgetStatusMeta.badgeClass}`}>
              {totalBudgetStatusMeta.badgeLabel}
            </span>
          </div>
          <div className="mb-3 flex items-center justify-center">
            <div
              className="relative flex h-36 w-36 items-center justify-center rounded-full p-[10px]"
              style={{
                background: `conic-gradient(${hexToRgba(totalRingAccentColor, 0.48)} ${Math.max(0, Math.min(totalBudgetStatus.percentage, 100))}%, #e9edf2 ${Math.max(0, Math.min(totalBudgetStatus.percentage, 100))}% 100%)`,
                boxShadow: `0 0 10px ${hexToRgba(totalRingAccentColor, 0.12)}`,
              }}
            >
              <div className="flex h-full w-full flex-col items-center justify-center rounded-full border border-white/80 bg-white shadow-inner">
                {totalBudgetAmount > 0 ? (
                  <>
                    <span className="text-3xl font-semibold text-gray-900">{Math.round(totalBudgetStatus.percentage)}%</span>
                    <span className="text-[11px] text-gray-500">used</span>
                  </>
                ) : (
                  <span className="text-2xl font-semibold text-gray-500">Setup</span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-1 flex items-end justify-between gap-3">
            <div className="space-y-0.5">
              <p className="text-sm text-gray-700">Budget: {formatCurrency(totalBudgetAmount)}</p>
              <p className="text-sm text-gray-700">Spent: {formatCurrency(totalSpent)}</p>
              <p className={`text-sm font-medium ${totalBudgetStatusMeta.textClass}`}>{totalBudgetStatusText}</p>
            </div>
            {!isEditingTotal && (
              <button
                type="button"
                onClick={handleEditTotal}
                className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 hover:bg-blue-100"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          {categoryStatusFilterOptions.map((option) => {
            const isActive = categoryStatusFilter === option.value;
            return (
              <button
                key={`status-filter-${option.value}`}
                type="button"
                onClick={() => setCategoryStatusFilter(option.value)}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                  isActive
                    ? 'border-blue-300 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
          {filteredOverviewMainCategoryTiles.map((tile) => (
            <div
              key={`overview-tile-${tile.id}`}
              className="relative rounded-2xl border border-gray-200 bg-white/90 p-4 text-left shadow-sm"
            >
              <div className="mb-2 space-y-1.5">
                <h3 className="text-base font-semibold leading-tight text-gray-900 whitespace-normal break-words sm:text-lg">{tile.name}</h3>
                <span className={`inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-[10px] font-semibold leading-tight ring-1 ring-inset sm:text-[11px] ${tile.statusMeta.badgeClass}`}>
                  {tile.statusMeta.badgeLabel}
                </span>
              </div>

              <div className="mb-3 flex items-center justify-center">
                <div
                  className="relative flex h-32 w-32 items-center justify-center rounded-full p-[8px] sm:h-36 sm:w-36 sm:p-[10px]"
                  style={{
                    background: `conic-gradient(${hexToRgba(tile.accentColor, 0.45)} ${Math.max(0, Math.min(tile.status.percentage, 100))}%, #e9edf2 ${Math.max(0, Math.min(tile.status.percentage, 100))}% 100%)`,
                    boxShadow: `0 0 9px ${hexToRgba(tile.accentColor, 0.1)}`,
                  }}
                >
                  <div className="flex h-full w-full flex-col items-center justify-center rounded-full border border-white/80 bg-white shadow-inner">
                    {tile.hasBudget ? (
                      <>
                        <span className="text-2xl font-semibold text-gray-900 sm:text-3xl">{Math.round(tile.status.percentage)}%</span>
                        <span className="text-[10px] text-gray-500 sm:text-[11px]">used</span>
                      </>
                    ) : (
                      <span className="text-xl font-semibold text-gray-500 sm:text-2xl">Setup</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-end justify-between gap-3">
                <div>
                  {tile.hasBudget ? (
                    <>
                      <p className="text-sm text-gray-700 tabular-nums">
                        {formatCurrency(tile.spentAmount)} / {formatCurrency(tile.budgetAmount)}
                      </p>
                      <p className={`text-sm font-medium ${tile.statusMeta.textClass}`}>{tile.deltaLabel}</p>
                    </>
                  ) : (
                    <p className="text-sm font-medium text-gray-500">Set a budget</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => openCategoryBudgetEditor(tile.id)}
                  className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 hover:bg-blue-100"
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
        {filteredOverviewMainCategoryTiles.length === 0 && (
          <p className="mt-3 text-sm text-gray-500">No categories match this status filter.</p>
        )}
      </div>

      {activeCategoryBudgetId && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-[2px]">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Category Budget"
            className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_24px_70px_-30px_rgba(15,23,42,0.55)] sm:max-w-xl sm:p-6"
          >
            <div className="mb-4 flex items-start justify-between gap-3 sm:mb-5">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 sm:text-xl">Category Budget</h3>
                <p className="mt-1 inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 sm:text-sm">
                  {activeCategoryBudgetName}
                </p>
              </div>
              <button
                type="button"
                onClick={closeCategoryBudgetEditor}
                disabled={isSavingCategoryBudget}
                className="rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
              >
                Close
              </button>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Budget Amount
              </label>
              <div className="flex items-center rounded-xl border border-slate-300 bg-white px-3 shadow-sm focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                <span className="text-lg font-medium text-slate-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={activeCategoryBudgetValue}
                  onChange={(event) => setActiveCategoryBudgetValue(event.target.value)}
                  className="w-full border-0 bg-transparent px-2 py-2.5 text-lg font-semibold text-slate-900 focus:outline-none"
                  placeholder="0.00"
                  autoFocus
                />
              </div>

              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-3 sm:mt-4 sm:px-4 sm:py-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
                    Actual Spend
                  </p>
                  <p className="text-[11px] text-slate-500 sm:text-xs">Prior 3 months</p>
                </div>
                {isLoadingRecentCategoryActuals ? (
                  <p className="text-xs text-slate-500">Loading history...</p>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      {(activeCategoryRecentSpending?.monthlyActuals || []).map((item) => (
                        <div
                          key={`actual-${item.key}`}
                          className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-center sm:px-3 sm:py-2.5"
                        >
                          <p className="text-[11px] font-medium text-slate-500 sm:text-xs">{item.label}</p>
                          <p className="mt-1 text-xs font-semibold text-slate-800 sm:text-sm">{formatCurrency(item.amount)}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 sm:px-3 sm:py-2.5">
                      <p className="text-[11px] font-medium uppercase tracking-wide text-blue-700 sm:text-xs">3-month average</p>
                      <p className="text-sm font-semibold text-blue-900 sm:text-base">
                        {formatCurrency(activeCategoryRecentSpending?.averageAmount || 0)}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2 sm:mt-6 sm:gap-2.5">
              <button
                type="button"
                onClick={closeCategoryBudgetEditor}
                disabled={isSavingCategoryBudget}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 sm:px-4"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCategoryBudget}
                disabled={isSavingCategoryBudget}
                className="rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-blue-400 sm:px-5"
              >
                {isSavingCategoryBudget ? 'Saving...' : 'Save budget'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isApplyFromSpendingOpen && (
        <div className="fixed inset-0 z-[120] overflow-hidden bg-black/40 p-4 sm:overflow-y-auto">
          <div className="mx-auto flex h-full w-full max-w-2xl flex-col gap-3 sm:min-h-full sm:justify-center">
            {!applyPreview && (
              <div className="shrink-0 rounded-xl bg-white p-5 shadow-2xl">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Use Prior Month Spending</h3>
                    <p className="text-sm text-gray-500">
                      Apply prior month spending as a starting budget for {getMonthName(selectedMonth)}.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeApplyFromSpendingModal}
                    className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                      Source Month
                    </label>
                    <input
                      type="month"
                      value={applySourceMonth}
                      onChange={(e) => setApplySourceMonth(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                      Apply To
                    </label>
                    <select
                      value={applyScope}
                      onChange={(e) => setApplyScope(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="all">All categories</option>
                      <option value="empty-only">Only categories without budget</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handlePreviewApplyFromSpending}
                    disabled={isLoadingApplyPreview || isApplyingFromSpending}
                    className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
                  >
                    {isLoadingApplyPreview ? 'Loading Preview...' : 'Preview'}
                  </button>
                </div>
              </div>
            )}

            {applyPreview && (
              <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-gray-200 bg-white shadow-2xl sm:max-h-[78vh] sm:flex-none">
                <div className="p-4 pb-3">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={handleBackFromApplyPreview}
                      className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={closeApplyFromSpendingModal}
                      className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                    <p className="font-medium text-gray-800">
                      {getMonthName(`${applyPreview.sourceYear}-${String(applyPreview.sourceMonth).padStart(2, '0')}`)}{' '}
                      -> {getMonthName(`${applyPreview.targetYear}-${String(applyPreview.targetMonth).padStart(2, '0')}`)}
                    </p>
                    <p className="text-gray-600">
                      {selectedApplySummary.selectedCount} selected, total{' '}
                      {formatCurrency(selectedApplySummary.selectedTotal)}
                    </p>
                  </div>
                  {applyPreview.summary.needsAutoTotalBudget && (
                    <p className="text-xs text-blue-700">
                      Total monthly budget will be auto-set to match applied category totals.
                    </p>
                  )}
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto border-t border-gray-200">
                  <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-gray-200 bg-white px-3 py-2 text-sm">
                    <label className="inline-flex items-center gap-2 text-gray-700">
                      <input
                        type="checkbox"
                        checked={areAllVisibleApplyCategoriesSelected}
                        onChange={toggleSelectAllApplyPreviewCategories}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        {areAllVisibleApplyCategoriesSelected ? 'Unselect all' : 'Select all'}
                      </span>
                    </label>
                    <span className="text-xs text-gray-500">
                      {selectedVisibleApplyCategoryIds.length}/{visibleApplyPreviewCategoryIds.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-[1.25rem_minmax(0,1fr)_5.5rem_6.75rem] items-center gap-2 border-b border-gray-200 bg-gray-50 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 sm:grid-cols-[1.25rem_minmax(0,1fr)_6.5rem_8rem] sm:gap-3 sm:px-3">
                    <span className="w-4 shrink-0" />
                    <span className="min-w-0">Category</span>
                    <span className="text-right">Prior Month</span>
                    <span className="text-right">Budget</span>
                  </div>

                  {applyPreviewGroupedCategories.map((group) => (
                    <div key={group.key} className="border-b border-gray-100 last:border-b-0">
                      {group.hasSubcategories && (
                        <div className="bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {group.parentName}
                        </div>
                      )}

                      {group.items.map((item) => {
                        const category = item.category;
                        const isSubcategory = Boolean(category?.parentCategoryId);

                        return (
                            <div
                              key={`apply-preview-${group.key}-${item.categoryId}`}
                              className="grid grid-cols-[1.25rem_minmax(0,1fr)_5.5rem_6.75rem] items-center gap-2 px-2 py-2 text-sm sm:grid-cols-[1.25rem_minmax(0,1fr)_6.5rem_8rem] sm:gap-3 sm:px-3"
                            >
                              <label className="inline-flex w-4 shrink-0 items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={selectedApplyCategoryIds.includes(String(item.categoryId))}
                                  onChange={() => toggleApplyPreviewCategorySelection(item.categoryId)}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                              </label>
                              <div className={`min-w-0 ${isSubcategory ? 'pl-4 sm:pl-6' : ''}`}>
                                <p className="truncate text-gray-700">
                                  {category?.name || 'Unknown category'}
                                </p>
                              </div>
                            <span className="text-right text-gray-500">{formatCurrency(item.sourceSpent)}</span>
                            <div className="flex w-full items-center rounded border border-gray-300 bg-white px-2">
                              <span className="text-gray-500">$</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={applyEditedAmounts[String(item.categoryId)] ?? String(Number(item.proposedAmount || 0))}
                                onChange={(event) =>
                                  handleApplyPreviewAmountChange(item.categoryId, event.target.value)
                                }
                                className="w-full border-0 bg-transparent px-1 py-1 text-right font-medium text-gray-900 focus:outline-none"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-gray-200 bg-white p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
                  <button
                    type="button"
                    onClick={closeApplyFromSpendingModal}
                    disabled={isApplyingFromSpending}
                    className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyFromSpending}
                    disabled={
                      isLoadingApplyPreview ||
                      isApplyingFromSpending ||
                      selectedVisibleApplyCategoryIds.length === 0
                    }
                    className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    {isApplyingFromSpending ? 'Applying...' : 'Apply Budget'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Budgeting Tips</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>- Set realistic budgets based on your past spending patterns</li>
          <li>- Category budgets help you control spending in specific areas</li>
          <li>- Review your budget monthly and adjust as needed</li>
          <li>- Try the 50/30/20 rule: 50% needs, 30% wants, 20% savings</li>
        </ul>
      </div>

      <StatusToast
        error={error}
        onClearError={() => setError('')}
      />
    </div>
  );
}

export default BudgetManager;
